import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ViewQuiltRoundedIcon from '@mui/icons-material/ViewQuiltRounded';

import {
  PAGE_TEMPLATE_IMPORT_MODES,
  PAGE_TEMPLATE_TYPES,
  listPageTemplates,
} from './foundation/pageTemplateRegistry';

const TYPE_LABELS = Object.freeze({
  [PAGE_TEMPLATE_TYPES.HOME]: 'Home',
  [PAGE_TEMPLATE_TYPES.ABOUT]: 'About',
  [PAGE_TEMPLATE_TYPES.SERVICES]: 'Services',
  [PAGE_TEMPLATE_TYPES.CONTACT]: 'Contact',
  [PAGE_TEMPLATE_TYPES.PROMO_PROGRAMS]: 'Promo Programs',
  [PAGE_TEMPLATE_TYPES.PRINT_SERVICES]: 'Print Services',
  [PAGE_TEMPLATE_TYPES.PORTFOLIO]: 'Portfolio / Work',
});

const TYPE_ORDER = Object.freeze([
  PAGE_TEMPLATE_TYPES.HOME,
  PAGE_TEMPLATE_TYPES.ABOUT,
  PAGE_TEMPLATE_TYPES.SERVICES,
  PAGE_TEMPLATE_TYPES.CONTACT,
  PAGE_TEMPLATE_TYPES.PROMO_PROGRAMS,
  PAGE_TEMPLATE_TYPES.PRINT_SERVICES,
  PAGE_TEMPLATE_TYPES.PORTFOLIO,
]);

export default function PageTemplateGallery({
  selectedPageType = PAGE_TEMPLATE_TYPES.HOME,
  onPageTypeChange,
  onImport,
  onBlankPage,
  onDuplicatePage,
  onGenerateWithAI,
  compact = false,
  allowedTypes,
}) {
  const [query, setQuery] = useState('');
  const [activeType, setActiveType] = useState(selectedPageType || PAGE_TEMPLATE_TYPES.HOME);
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [importMode, setImportMode] = useState(PAGE_TEMPLATE_IMPORT_MODES.APPEND);

  React.useEffect(() => {
    if (selectedPageType && selectedPageType !== activeType) setActiveType(selectedPageType);
  }, [activeType, selectedPageType]);

  const availableTypes = useMemo(() => {
    const requested = Array.isArray(allowedTypes) && allowedTypes.length ? allowedTypes : TYPE_ORDER;
    return TYPE_ORDER.filter((type) => requested.includes(type));
  }, [allowedTypes]);

  React.useEffect(() => {
    if (!availableTypes.includes(activeType) && availableTypes[0]) {
      setActiveType(availableTypes[0]);
    }
  }, [activeType, availableTypes]);

  const templates = useMemo(
    () => listPageTemplates({ type: activeType, query }),
    [activeType, query]
  );

  const changeType = (type) => {
    setActiveType(type);
    onPageTypeChange?.(type);
  };

  const confirmImport = () => {
    if (!pendingTemplate) return;
    onImport?.(pendingTemplate, importMode);
    setPendingTemplate(null);
    setImportMode(PAGE_TEMPLATE_IMPORT_MODES.APPEND);
  };

  return (
    <Box className={`ps-page-template-library${compact ? ' is-compact' : ''}`}>
      <Box className="ps-page-template-library__header">
        <Box>
          <Typography className="ps-page-template-library__title">Ready-made layouts</Typography>
          <Typography variant="body2" className="ps-builder-panel-copy">
            Choose a polished starting point, then customize every section on this page.
          </Typography>
        </Box>
        <Box className="ps-page-template-library__quick-actions">
          {onBlankPage ? (
            <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} onClick={onBlankPage}>
              Blank
            </Button>
          ) : null}
          {onDuplicatePage ? (
            <Button size="small" variant="outlined" startIcon={<ContentCopyRoundedIcon />} onClick={onDuplicatePage}>
              Duplicate
            </Button>
          ) : null}
          {onGenerateWithAI ? (
            <Button size="small" variant="outlined" startIcon={<AutoAwesomeRoundedIcon />} onClick={onGenerateWithAI}>
              Generate with AI
            </Button>
          ) : null}
        </Box>
      </Box>

      {availableTypes.length > 1 ? (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={activeType}
          onChange={(_event, value) => value && changeType(value)}
          className="ps-page-template-library__types"
        >
          {availableTypes.map((type) => (
            <ToggleButton key={type} value={type}>{TYPE_LABELS[type]}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      ) : null}

      <TextField
        size="small"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={`Search ${TYPE_LABELS[activeType] || 'page'} layouts`}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment>
          ),
        }}
      />

      <Box className="ps-page-template-library__grid">
        {templates.map((template) => (
          <Box key={template.id} className="ps-page-template-card">
            <Box className="ps-page-template-card__visual">
              <ViewQuiltRoundedIcon />
              <Chip size="small" label={template.accent || TYPE_LABELS[template.type]} />
            </Box>
            <Box className="ps-page-template-card__body">
              <Typography className="ps-page-template-card__name">{template.name}</Typography>
              <Typography variant="caption" className="ps-builder-panel-copy">
                {template.description}
              </Typography>
              <Box className="ps-page-template-card__tags">
                {(template.tags || []).slice(0, 3).map((tag) => <Chip size="small" key={tag} label={tag} variant="outlined" />)}
              </Box>
              <Button size="small" variant="contained" fullWidth onClick={() => setPendingTemplate(template)}>
                Add this layout
              </Button>
            </Box>
          </Box>
        ))}
      </Box>

      {!templates.length ? (
        <Box className="ps-builder-empty-panel">
          <Typography sx={{ fontWeight: 800 }}>No matching layouts</Typography>
          <Typography variant="body2" className="ps-builder-panel-copy">Try another search.</Typography>
        </Box>
      ) : null}

      <Dialog open={Boolean(pendingTemplate)} onClose={() => setPendingTemplate(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add {pendingTemplate?.name || 'layout'} to this page</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Add this layout after your current content, or replace everything currently on this page for a fresh start.
          </Typography>
          <TextField
            select
            label="How should it be added?"
            value={importMode}
            onChange={(event) => setImportMode(event.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value={PAGE_TEMPLATE_IMPORT_MODES.APPEND}>Add after current blocks (recommended)</MenuItem>
            <MenuItem value={PAGE_TEMPLATE_IMPORT_MODES.REPLACE}>Replace everything on this page</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingTemplate(null)}>Cancel</Button>
          <Button variant="contained" color={importMode === PAGE_TEMPLATE_IMPORT_MODES.REPLACE ? 'warning' : 'primary'} onClick={confirmImport}>
            {importMode === PAGE_TEMPLATE_IMPORT_MODES.REPLACE ? 'Replace page' : 'Add layout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
